// SatisfactionSurvey utilities - extracted from presentation layer
// All utility functions, constants, and type definitions for SatisfactionSurvey

import * as Api from '@ViewModels';
import * as yup from 'yup';
import {
	createNotificationSettingsSchema,
	createTagGenerationSettingsSchema,
	createBaseSurveySchema,
	createCustomFormFieldSchema,
} from '../shared/validation/schemas';

// Critical constants used across SatisfactionSurvey components
export const QUESTION_LIMIT = 20;
export const STAR_RATING_LIMIT = 5;
export const DEFAULT_NOTIFICATION_RATINGS = [1, 2, 3, 4, 5] as const;
export const DEFAULT_CONDITIONAL_RATINGS = [1, 2, 3] as const;

// Tab values for consistency with other survey types
export const TabValues = {
	Questions: 'Questions',
	Settings: 'Settings',
	Preview: 'Preview',
} as const;

export type TabValues = (typeof TabValues)[keyof typeof TabValues];

// Form type for SatisfactionSurvey (following other survey patterns)
// Omits server-generated fields that aren't needed during form editing
export type SatisfactionSurveyFormData = Omit<
	Api.ISatisfactionSurvey,
	'_type' | 'id' | 'anonymousLink' | 'archivedDate' | 'notificationSettings' | 'reviewRequestSettings'
> & {
	// Override nested types to remove _type requirements
	notificationSettings?: {
		isEnabled?: boolean;
		ratings?: number[];
		additionalRecipients?: string[];
	};
	reviewRequestSettings?: {
		isEnabled?: boolean;
		ratings?: number[];
		reviewRequest?: string;
		reviewType?: string;
		reviewUrl?: string;
	};
};

// Interface for satisfaction form data used by shared components
export interface ISatisfactionFormData {
	tagGenerationSettings: {
		tagOnCompletion: boolean;
		completionTag: string;
		customFormFieldTagSettings: Api.ITagGenerationSettings[];
		effectiveCompletionTag?: Api.IAccountTag;
	};
}

// Validation schema for SatisfactionSurvey using shared schemas
export const validationSchema = yup.object({
	// Base survey fields using shared schema
	...createBaseSurveySchema({
		requireIntro: true,
		requireStartDate: true,
		requireExpirationDate: true,
	}),

	// SatisfactionSurvey-specific fields
	companyDisplayName: yup.string().trim().required('Company name is required'),

	// Custom form with SatisfactionSurvey-specific requirements
	customForm: yup
		.object({
			name: yup.string().optional(),
			fields: createCustomFormFieldSchema({
				questionLimit: QUESTION_LIMIT,
				requireLabels: true,
			}),
		})
		.optional(),

	// SatisfactionSurvey-specific metadata for conditional questions
	customFormMetaData: yup
		.array()
		.of(
			yup.object({
				fieldId: yup.string().required(),
				isConditional: yup.boolean().optional(),
				ratings: yup.array().of(yup.number()).optional(),
			})
		)
		.optional(),

	// Shared notification settings with ratings for SatisfactionSurvey
	notificationSettings: createNotificationSettingsSchema({
		includeRatings: true,
		ratingRange: { min: 1, max: STAR_RATING_LIMIT },
	}).optional(),

	// SatisfactionSurvey-specific review request settings
	reviewRequestSettings: yup
		.object({
			isEnabled: yup.boolean().optional().default(false),
			ratings: yup
				.array()
				.of(yup.number().required('Review rating is required'))
				.test('review-ratings-length', 'Review ratings must be between 5 and 1', function (value) {
					const { isEnabled } = this.parent || {};
					if (!isEnabled) {
						return true;
					}
					return value.length >= 1 && value.length <= STAR_RATING_LIMIT;
				}),
			reviewRequest: yup.string().required('Review request message is required'),
			reviewType: yup.string().test('reviewRequestSettings.isEnabled-type', function (val) {
				const { isEnabled } = this.parent || {};
				if (!isEnabled || val) {
					return true;
				}
				return this.createError({ message: 'Review type is required' });
			}),
			reviewUrl: yup.string().test('reviewRequestSettings.isEnabled-url', function (val) {
				const { isEnabled } = this.parent || {};
				if (!isEnabled || val) {
					return true;
				}
				return this.createError({ message: 'Review URL is required' });
			}),
		})
		.optional(),

	// Shared tag generation settings
	tagGenerationSettings: createTagGenerationSettingsSchema().optional(),
});

// Consolidated constants for easy import
export const SATISFACTION_SURVEY_CONSTANTS = {
	QUESTION_LIMIT,
	STAR_RATING_LIMIT,
	DEFAULT_NOTIFICATION_RATINGS,
	DEFAULT_CONDITIONAL_RATINGS,
} as const;

export type SatisfactionSurveyConstants = typeof SATISFACTION_SURVEY_CONSTANTS;
